import React, { useState } from 'react';
import api from '../../services/api';
import { showError } from '../../utils/toast'; // #204
import StateBlock from '../ui/StateBlock';

/**
 * Seção de evidências anexadas com upload e listagem.
 *
 * Download (#64 Camada 2): usa o endpoint AUTENTICADO
 * `GET /api/nc/arquivo/{id}/conteudo` (serve do banco — durável, sobrevive a
 * redeploy do Railway). O link direto antigo (`${API_URL}/${filepath}`)
 * apontava pro mount estático `/uploads`, que foi REMOVIDO na Sessão 17 →
 * download quebrado. Corrigido aqui; vale pra evidência do tenant E do
 * fornecedor (que chega via upload público marcado origem='fornecedor').
 *
 * @param {number} ncId - ID da NC
 * @param {string} currentStatus - Status atual (para permitir edição)
 * @param {array} arquivos - Lista de arquivos anexados
 * @param {function} onFileUpload - Callback para upload de arquivo
 * @param {function} onDeleteFile - Callback para exclusão de arquivo
 * @param {boolean} uploading - Estado de upload em andamento
 */
function NCResultEvidences({
  ncId,
  currentStatus,
  arquivos,
  onFileUpload,
  onDeleteFile,
  uploading,
}) {
  const [baixandoId, setBaixandoId] = useState(null);

  const abrirArquivo = async (arq) => {
    if (baixandoId) return;
    setBaixandoId(arq.id);
    try {
      const res = await api.get(`/api/nc/arquivo/${arq.id}/conteudo`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Libera o object URL depois que a nova aba já carregou.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showError('Não foi possível abrir o arquivo. Tente novamente.');
    } finally {
      setBaixandoId(null);
    }
  };

  return (
    <section className="result-section">
      <h3><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>attach_file</span>Evidências Anexadas</h3>

      {arquivos.length === 0 && (
        <StateBlock kind="empty" icon="attachment" message="Nenhuma evidência anexada." />
      )}

      {arquivos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {arquivos.map((arq) => {
            const ehImagem = arq.filename?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
            return (
              <div key={arq.id} style={{
                padding: '12px',
                background: 'var(--surface-low, #f0f3ff)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.5rem', color: 'var(--on-secondary, #57657a)' }}>
                  {ehImagem ? 'image' :
                   arq.filename?.match(/\.(pdf)$/i) ? 'picture_as_pdf' :
                   arq.filename?.match(/\.(xls|xlsx|csv)$/i) ? 'table_chart' :
                   arq.filename?.match(/\.(doc|docx)$/i) ? 'description' : 'attach_file'}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={() => abrirArquivo(arq)}
                    disabled={baixandoId === arq.id}
                    title="Abrir / baixar"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--primary, #006948)',
                      textAlign: 'left',
                      textDecoration: 'underline',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {baixandoId === arq.id ? 'Abrindo…' : arq.filename}
                  </button>

                  {arq.uploaded_at && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--on-secondary, #57657a)',
                    }}>
                      Anexado em: {new Date(arq.uploaded_at).toLocaleDateString('pt-BR')}
                    </div>
                  )}

                  {/* #142 — selo distinguindo evidência enviada pelo fornecedor
                      (via link público #64 Camada 2) das anexadas pelo tenant. */}
                  {arq.origem === 'fornecedor' && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '6px',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: '#e0f2fe',
                      color: '#075985',
                      border: '1px solid #bae6fd',
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>local_shipping</span>
                      {/* #155 — autoria: nome do fornecedor quando houver; fallback
                          genérico pras evidências antigas (enviado_por_fornecedor NULL). */}
                      {arq.enviado_por_fornecedor
                        ? `Enviado por ${arq.enviado_por_fornecedor}`
                        : 'Enviado pelo fornecedor'}
                    </span>
                  )}
                </div>

                {currentStatus === 'rascunho' && (
                  <button
                    onClick={() => onDeleteFile(arq.id)}
                    title="Excluir arquivo"
                    style={{
                      background: 'var(--error-container, #ffdad6)',
                      color: 'var(--on-error-container, #93000a)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'var(--primary, #006948)',
            color: '#fff',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
            {uploading ? 'hourglass_top' : 'upload_file'}
          </span>
          {uploading ? 'Enviando...' : 'Anexar Evidência'}
          <input
            type="file"
            multiple
            onChange={onFileUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
      </div>
    </section>
  );
}

export default NCResultEvidences;
